<template>
    <div class="root">
        <AppPlaceholder v-if="isRequestProcessing" />

        <main v-else-if="hideNavbar" class="scroll-container public" data-qa="public-content">
            <router-view />
        </main>

        <div v-else-if="bootLoadError" class="page-overlay">
            <BootLoadError />
        </div>

        <template v-else-if="bootLoadSuccess">
            <main class="scroll-container" data-qa="content">
                <NavHeader />
                <router-view />
            </main>

            <AnalyticsHandler />
            <DsToastContainer />
        </template>
    </div>
</template>

<script>

import { onMounted, onUnmounted } from 'vue';
import { mapState } from 'vuex';

import { DsToastContainer } from '@infusionsoft/design-system';
import AppPlaceholder from '@/components/AppPlaceholder.vue';
import AnalyticsHandler from '@/analytics/components/AnalyticsHandler.vue';
import BootLoadError from '@/components/BootLoadError.vue';
import NavHeader from '@/components/NavHeader.vue';
import { ERROR, SUCCESS } from '@/shared/constants/loadingStatuses.constants';
import { useAuth0 } from '@auth0/auth0-vue';
import { usePendo } from '@/analytics/pendo';
import { authCallbackError, clearAuth0LocalStorage } from '@/plugins/auth';

/**
 * AUTH-9445: Rebranding of in AC Web of the Keap Logos to Thryv
 * Replaces Keap green (#36A635) with Thryv orange (#FF5000) in DsIllustration inline SVGs.
 * The design system has the green color hardcoded in SVG source files and we can't modify the package.
 */
function useIllustrationColorOverride() {
    const GREEN = '#36A635';
    const ORANGE = '#FF5000';

    function replaceColorsInSvg(svg) {
        svg.querySelectorAll(`[fill="${GREEN}" i], [stroke="${GREEN}" i]`).forEach((el) => {
            if (el.getAttribute('fill')?.toUpperCase() === GREEN) {
                el.setAttribute('fill', ORANGE);
            }

            if (el.getAttribute('stroke')?.toUpperCase() === GREEN) {
                el.setAttribute('stroke', ORANGE);
            }
        });
    }

    let observer = null;

    onMounted(() => {
        document.body.querySelectorAll('svg').forEach(replaceColorsInSvg);

        observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== Node.ELEMENT_NODE) continue;

                    if (node.tagName === 'svg') {
                        replaceColorsInSvg(node);
                    } else {
                        node.querySelectorAll?.('svg')?.forEach(replaceColorsInSvg);
                    }
                }
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });
    });

    onUnmounted(() => {
        observer?.disconnect();
    });
}

export default {
    components: {
        AppPlaceholder,
        AnalyticsHandler,
        BootLoadError,
        NavHeader,
        DsToastContainer,
    },

    setup() {
        const auth = useAuth0();

        usePendo().attachScripts();
        useIllustrationColorOverride();

        return { auth };
    },

    watch: {
        'auth.isAuthenticated.value': {
            handler(newValue, oldValue) {
                if (newValue && !oldValue) {
                    this.$store.dispatch('global/LOAD_BOOT');
                } else if (!newValue && oldValue) {
                    clearAuth0LocalStorage();

                    if (!this.$route.meta?.isPublic && !authCallbackError) {
                        this.auth.logout({
                            logoutParams: {
                                returnTo: window.location.origin,
                            },
                        });
                    }
                }
            },
            immediate: true,
        },
    },

    computed: {
        ...mapState({
            bootStatus: ({ global }) => global.boot.status,
        }),

        isRequestProcessing() {
            return !this.$route.meta?.title || this.auth.isLoading.value;
        },

        bootLoadSuccess() {
            return this.bootStatus === SUCCESS;
        },

        bootLoadError() {
            return !this.auth.isAuthenticated.value || this.bootStatus === ERROR;
        },

        hideNavbar() {
            return Boolean(this.$route.meta?.isPublic) || Boolean(this.$route.meta?.isInvite) || Boolean(this.$route.meta?.isOAuth) || Boolean(this.$route.meta?.hideNavbar);
        },
    },
};
</script>

<style lang="scss" scoped>
.root {
    height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: $color-paper;
}

.scroll-container {
    @include overflowX;
    @include overflowY;
    flex: auto;
    width: 100%;
    max-width: 100%;
    position: relative;
}

.page-overlay {
    position: fixed;
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
